import get from 'lodash/get'
import { useCallback, useEffect, useRef, useState } from 'react'

// Services
import baseAxios from 'axios'
import axios from 'services/axios'

import useMounted from 'use/mounted'

const CancelToken = baseAxios.CancelToken

export const useTable = ({
  url,
  model,
  filters = {},
  method = 'GET',
  getDataPath = 'data',
  getTotalPath = 'paging.total',
  defaultLimit = 10,
  sortByDefault = null,
  sortDirectionDefault = null,
}) => {
  const isMounted = useMounted()
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(defaultLimit)
  const [sortBy, setSortBy] = useState(sortByDefault)
  const [sortDirection, setSortDirection] = useState(sortDirectionDefault)
  const [tableFilters, setTableFilters] = useState(filters)

  const cancelSource = useRef(null)

  const getTableData = useCallback(async () => {
    if (cancelSource.current) {
      cancelSource.current.cancel()
    }
    cancelSource.current = CancelToken.source()
    try {
      setLoading(true)

      // Prepare request config
      const request = {
        url,
        method,
        cancelToken: cancelSource.current.token,
      }

      // Set request payload
      request[request.method === 'GET' ? 'params' : 'data'] = {
        sortBy,
        sortDirection,
        page,
        limit,
        ...tableFilters,
      }

      const res = await axios.request(request)

      const list = get(res, getDataPath, []).map((x) => {
        return model ? new model(x) : x
      })
      const total = get(res, getTotalPath, 0)

      if (isMounted.current) {
        // Clear data
        setData([])
        setData(list)
        setTotal(total)
      }
    } catch (error) {
      //
    } finally {
      if (isMounted.current) {
        setLoading(false)
      }
    }
  }, [
    url,
    method,
    sortBy,
    sortDirection,
    page,
    limit,
    tableFilters,
    getDataPath,
    getTotalPath,
    isMounted,
    model,
  ])

  const onSortChange = (sortBy, sortDirection) => {
    setSortBy(sortBy)
    setSortDirection(sortDirection)
    setPage(1)
  }

  const onPageChange = (newPage) => {
    setPage(newPage)
  }

  useEffect(() => {
    setTableFilters(filters)
    setPage(1)
  }, [filters])

  useEffect(() => {
    getTableData()
  }, [
    tableFilters,
    page,
    method,
    model,
    limit,
    sortBy,
    sortDirection,
    url,
    getDataPath,
    getTotalPath,
    getTableData,
  ])

  return {
    loading,
    data,
    total,
    page,
    limit,
    sortBy,
    sortDirection,
    setLoading,
    setLimit,
    setPage,
    onSortChange,
    onPageChange,
    getTableData,
  }
}

export default useTable
